import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Check, X, Trash2, Award, Loader2, Plus, ExternalLink, Archive } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingReview, setAddingReview] = useState(false);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(null); // 'delete' | 'archive' | null
  
  // Form per nuova recensione manuale
  const [newReview, setNewReview] = useState({
    name: '',
    text: '',
    rating: 5,
    event_service: '',
    source: 'Google',
    review_date: ''
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    avgRating: 0
  });

  useEffect(() => {
    fetchReviews();
  }, [statusFilter, sourceFilter]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter, sourceFilter]);

  const fetchReviews = async () => {
    try {
      let params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const response = await api.get(`/admin/reviews?${params.toString()}`);
      const data = response.data;
      setReviews(data);
      
      // Calcola statistiche
      const approved = data.filter(r => r.status === 'approvata');
      setStats({
        total: data.length,
        pending: data.filter(r => r.status === 'in_attesa').length,
        approved: approved.length,
        avgRating: approved.length > 0 
          ? (approved.reduce((acc, r) => acc + r.rating, 0) / approved.length).toFixed(1)
          : 0
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const updateReviewStatus = async (reviewId, status, is_featured = null) => {
    setUpdating(reviewId);
    try {
      let url = `/admin/reviews/${reviewId}/status?status=${status}`;
      if (is_featured !== null) {
        url += `&is_featured=${is_featured}`;
      }
      await api.put(url);
      toast.success(status === 'approvata' ? 'Recensione approvata' : status === 'rifiutata' ? 'Recensione rifiutata' : 'Stato aggiornato');
      fetchReviews();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setUpdating(null);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa recensione?')) return;

    try {
      await api.delete(`/admin/reviews/${reviewId}`);
      toast.success('Recensione eliminata');
      fetchReviews();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Bulk Actions
  const toggleSelectAll = () => {
    if (selectedIds.length === reviews.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(reviews.map(r => r.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const bulkDelete = async () => {
    setBulkLoading(true);
    try {
      await api.post('/admin/reviews/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} recensioni eliminate`);
      setSelectedIds([]);
      setShowBulkConfirm(null);
      fetchReviews();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Errore nell\'eliminazione');
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkArchive = async () => {
    setBulkLoading(true);
    try {
      await api.post('/admin/reviews/bulk-archive', { ids: selectedIds });
      toast.success(`${selectedIds.length} recensioni archiviate`);
      setSelectedIds([]);
      setShowBulkConfirm(null);
      fetchReviews();
    } catch (error) {
      console.error('Bulk archive error:', error);
      toast.error('Errore nell\'archiviazione');
    } finally {
      setBulkLoading(false);
    }
  };

  const addManualReview = async () => {
    if (!newReview.name || !newReview.text) {
      toast.error('Nome e testo sono obbligatori');
      return;
    }

    setAddingReview(true);
    try {
      await api.post('/admin/reviews', newReview);
      toast.success('Recensione aggiunta con successo');
      setShowAddModal(false);
      setNewReview({
        name: '',
        text: '',
        rating: 5,
        event_service: '',
        source: 'Google',
        review_date: ''
      });
      fetchReviews();
    } catch (error) {
      console.error('Add error:', error);
      toast.error('Errore nell\'aggiunta della recensione');
    } finally {
      setAddingReview(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      in_attesa: 'bg-yellow-100 text-yellow-700',
      approvata: 'bg-green-100 text-green-700',
      rifiutata: 'bg-red-100 text-red-700'
    };
    const labels = {
      in_attesa: 'In Attesa',
      approvata: 'Approvata',
      rifiutata: 'Rifiutata'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${variants[status] || variants.in_attesa}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getSourceBadge = (source, isManual) => {
    const colors = {
      'Sito Web': 'bg-blue-100 text-blue-700',
      'Google': 'bg-red-100 text-red-700',
      'Matrimonio.com': 'bg-pink-100 text-pink-700',
      'ProntoPro': 'bg-orange-100 text-orange-700',
      'Facebook': 'bg-indigo-100 text-indigo-700',
      'Altro': 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[source] || colors['Altro']}`}>
        {isManual && <ExternalLink size={10} className="inline mr-1" />}
        {source}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-2">Recensioni</h1>
          <p className="text-muted-foreground">Modera e gestisci le recensioni dei clienti</p>
        </div>
        
        {/* Add Manual Review Button */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button data-testid="add-review-btn">
              <Plus size={16} className="mr-2" />
              Aggiungi Manualmente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Aggiungi Recensione Manuale</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="mb-2 block">Nome cliente *</Label>
                <Input
                  value={newReview.name}
                  onChange={(e) => setNewReview(r => ({...r, name: e.target.value}))}
                  placeholder="Mario Rossi"
                />
              </div>
              
              <div>
                <Label className="mb-2 block">Fonte *</Label>
                <Select value={newReview.source} onValueChange={(v) => setNewReview(r => ({...r, source: v}))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Google">Google</SelectItem>
                    <SelectItem value="Matrimonio.com">Matrimonio.com</SelectItem>
                    <SelectItem value="ProntoPro">ProntoPro</SelectItem>
                    <SelectItem value="Facebook">Facebook</SelectItem>
                    <SelectItem value="Altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">Servizio / Evento</Label>
                <Input
                  value={newReview.event_service}
                  onChange={(e) => setNewReview(r => ({...r, event_service: e.target.value}))}
                  placeholder="Matrimonio, Ritratto..."
                />
              </div>

              <div>
                <Label className="mb-2 block">Valutazione *</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setNewReview(r => ({...r, rating}))}
                      className="p-1"
                    >
                      <Star
                        size={24}
                        fill={rating <= newReview.rating ? '#facc15' : 'none'}
                        className={rating <= newReview.rating ? 'text-yellow-400' : 'text-gray-300'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Testo recensione *</Label>
                <Textarea
                  value={newReview.text}
                  onChange={(e) => setNewReview(r => ({...r, text: e.target.value}))}
                  placeholder="Scrivi il testo della recensione..."
                  rows={4}
                />
              </div>

              <div>
                <Label className="mb-2 block">Data recensione (opzionale)</Label>
                <Input
                  type="date"
                  value={newReview.review_date}
                  onChange={(e) => setNewReview(r => ({...r, review_date: e.target.value}))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Annulla
              </Button>
              <Button onClick={addManualReview} disabled={addingReview}>
                {addingReview ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  'Aggiungi Recensione'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Totali</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Da approvare</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Approvate</p>
            <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 flex items-center gap-2">
            <Star size={20} fill="#facc15" className="text-yellow-400" />
            <div>
              <p className="text-sm text-muted-foreground">Rating medio</p>
              <p className="text-2xl font-bold">{stats.avgRating}/5</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="reviews-status-filter">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="in_attesa">In Attesa</SelectItem>
            <SelectItem value="approvata">Approvate</SelectItem>
            <SelectItem value="rifiutata">Rifiutate</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-48" data-testid="reviews-source-filter">
            <SelectValue placeholder="Filtra per fonte" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le fonti</SelectItem>
            <SelectItem value="Sito Web">Sito Web</SelectItem>
            <SelectItem value="Google">Google</SelectItem>
            <SelectItem value="Matrimonio.com">Matrimonio.com</SelectItem>
            <SelectItem value="ProntoPro">ProntoPro</SelectItem>
            <SelectItem value="Facebook">Facebook</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {reviews.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-muted/50 rounded">
          <Checkbox
            checked={selectedIds.length === reviews.length && reviews.length > 0}
            onCheckedChange={toggleSelectAll}
            data-testid="select-all-reviews"
          />
          <span className="text-sm text-muted-foreground">
            {selectedIds.length > 0 ? `${selectedIds.length} selezionate` : 'Seleziona tutto'}
          </span>
          
          {selectedIds.length > 0 && (
            <div className="flex gap-2 ml-auto">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkConfirm('archive')}
                disabled={bulkLoading}
              >
                <Archive size={14} className="mr-1" />
                Archivia ({selectedIds.length})
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowBulkConfirm('delete')}
                disabled={bulkLoading}
              >
                <Trash2 size={14} className="mr-1" />
                Elimina ({selectedIds.length})
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Bulk Confirm Modal */}
      <Dialog open={!!showBulkConfirm} onOpenChange={() => setShowBulkConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showBulkConfirm === 'delete' ? 'Conferma Eliminazione' : 'Conferma Archiviazione'}
            </DialogTitle>
          </DialogHeader>
          <p className="py-4">
            Sei sicuro di voler {showBulkConfirm === 'delete' ? 'eliminare' : 'archiviare'}{' '}
            <strong>{selectedIds.length}</strong> recensioni?
            {showBulkConfirm === 'delete' && (
              <span className="block text-destructive text-sm mt-2">Questa azione non può essere annullata.</span>
            )}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(null)}>Annulla</Button>
            <Button
              variant={showBulkConfirm === 'delete' ? 'destructive' : 'default'}
              onClick={showBulkConfirm === 'delete' ? bulkDelete : bulkArchive}
              disabled={bulkLoading}
            >
              {bulkLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
              {showBulkConfirm === 'delete' ? 'Elimina' : 'Archivia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border p-6"
            data-testid={`review-item-${review.id}`}
          >
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              {/* Checkbox */}
              <Checkbox
                checked={selectedIds.includes(review.id)}
                onCheckedChange={() => toggleSelect(review.id)}
                className="mt-1"
              />
              
              {/* Content */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <h3 className="font-medium">{review.name}</h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground">{review.event_service || 'N/A'}</span>
                  {getStatusBadge(review.status)}
                  {getSourceBadge(review.source, review.is_manual)}
                  {review.is_featured && (
                    <span className="flex items-center gap-1 text-yellow-600 text-xs">
                      <Award size={12} />
                      Featured
                    </span>
                  )}
                </div>

                <div className="flex gap-0.5 mb-3 text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      fill={i < review.rating ? 'currentColor' : 'none'}
                    />
                  ))}
                </div>

                <p className="text-muted-foreground">{review.text}</p>

                <p className="text-xs text-muted-foreground mt-3">
                  {new Date(review.created_at).toLocaleDateString('it-IT')}
                  {review.is_manual && <span className="ml-2">• Inserita manualmente</span>}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {review.status === 'in_attesa' ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => updateReviewStatus(review.id, 'approvata')}
                      disabled={updating === review.id}
                      data-testid={`approve-review-${review.id}`}
                    >
                      {updating === review.id ? (
                        <Loader2 size={14} className="mr-1 animate-spin" />
                      ) : (
                        <Check size={14} className="mr-1" />
                      )}
                      Approva
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateReviewStatus(review.id, 'rifiutata')}
                      disabled={updating === review.id}
                      className="text-destructive"
                      data-testid={`reject-review-${review.id}`}
                    >
                      <X size={14} className="mr-1" />
                      Rifiuta
                    </Button>
                  </>
                ) : (
                  <>
                    {review.status === 'approvata' && (
                      <Button
                        variant={review.is_featured ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateReviewStatus(review.id, review.status, !review.is_featured)}
                        disabled={updating === review.id}
                        data-testid={`feature-review-${review.id}`}
                      >
                        <Award size={14} className="mr-1" />
                        {review.is_featured ? 'Rimuovi evidenza' : 'In evidenza'}
                      </Button>
                    )}
                    <Select
                      value={review.status}
                      onValueChange={(value) => updateReviewStatus(review.id, value)}
                      disabled={updating === review.id}
                    >
                      <SelectTrigger className="w-32 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_attesa">In Attesa</SelectItem>
                        <SelectItem value="approvata">Approvata</SelectItem>
                        <SelectItem value="rifiutata">Rifiutata</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteReview(review.id)}
                  className="text-destructive"
                  data-testid={`delete-review-${review.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {reviews.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          Nessuna recensione trovata con i filtri selezionati
        </div>
      )}
    </div>
  );
}
